import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { assignedEmployeesExportService } from '@/api/domains/securityGrouping';
import type { AssignedEmployeesStatusFilter } from '@/api/domains/securityGrouping';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import type { UIExportFormat } from '@/types/fileExport.types';


export interface UseAssignedEmployeesExportParams {
  status: AssignedEmployeesStatusFilter;
}

interface UseAssignedEmployeesExportResult {
  exportAssignedEmployees: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

export function useAssignedEmployeesExport(
  params: UseAssignedEmployeesExportParams,
): UseAssignedEmployeesExportResult {
  const { t: translate } = useTranslation();
  const { showError } = useUI();

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => assignedEmployeesExportService.export({ status: params.status }),
    successMessage: translate('securityGrouping:assignedEmployees.messages.exportSuccess'),
    errorMessage: translate('securityGrouping:assignedEmployees.messages.exportError'),
  });

  const exportAssignedEmployees = useCallback(
    async (format: UIExportFormat) => {
      if (format !== 'EXCEL') {
        showError(translate('securityGrouping:assignedEmployees.messages.exportError'));
        return;
      }
      await exportFile();
    },
    [exportFile, showError, translate],
  );

  return { exportAssignedEmployees, isExporting };
}
